import React, { useEffect, useState } from "react";
import DACIPagination from "components/DACIPagination";
import { Pagination, Stack, Alert, AlertTitle, Button, IconButton, Box, Snackbar, Grid, useTheme, useMediaQuery } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PropTypes from "prop-types";
import usePermission from "hooks/usePermission";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useMaterialUIController } from "context";
import colors from "assets/theme/base/colors";

export default function EvidenceList({
  handleEvidenceCreate,
  handleEvidenceList,
  handleRoleDelete,
  entriesStart,
  entriesEnd,
  totalData,
  totalPages,
  currentPage,
  handlePageChange,
}) {

  let { white, text } = colors;

  const iconColor = text.main; // #7b809a - mismo color que el texto de las tablas
  white = white.main;

  // Obtener el estado del tema (modo oscuro/claro)
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Hook para detectar breakpoints responsivos
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 960px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  // Verificar permisos
  const canCreate = usePermission("Crear", "Evidencias");
  const canEdit = usePermission("Editar", "Evidencias");
  const canDelete = usePermission("Eliminar", "Evidencias");
  const canListParticipant = usePermission("Listar", "Participantes");

  // Estado para controlar la visibilidad del Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Función para verificar si la URL existe
  const checkUrlExists = async (url) => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  // Definir las columnas para la tabla
  const columns = [
    { Header: "Descripción", accessor: "description", width: "35%", align: "left" },
    { Header: "Archivo", accessor: "file", width: "30%", align: "left" },
    { Header: "Fecha Creación", accessor: "created", align: "left" },
    { Header: "Url", accessor: "url", align: "left" },
    { Header: "Acciones", accessor: "actions", align: "center" },
  ];

  // Construir las filas basadas en la lista de evidencias
  const rows = handleEvidenceList
    ? handleEvidenceList.map((evidence) => ({
      description: (
        <MDTypography
          variant="caption"
          style={{
            wordBreak: "break-word", // Asegura que las palabras largas se rompan
            whiteSpace: "normal", // Permite múltiples líneas en el texto
            overflow: "hidden", // Evita el desbordamiento
            textOverflow: "ellipsis", // Agrega puntos suspensivos si es necesario
            display: "flex", // Para usar flexbox
            alignItems: "center", // Centrado vertical
            height: "100%", // Ocupar toda la altura de la celda
            width: "100%", // Ocupar toda el ancho de la celda
          }}>
          {evidence.description}
        </MDTypography>
      ),
      file: <MDTypography variant="caption">{decodeURIComponent(evidence.file)}</MDTypography>, // Nombre del archivo decodificado
      created: (
        <MDTypography variant="caption">
          {new Date(evidence.created).toLocaleString()}
        </MDTypography>
      ),
      url: (
        <a
          href="#"
          onClick={async (e) => {
            e.preventDefault();
            const url = process.env.REACT_APP_BACKEND_URL_EVIDENCE + evidence.url;
            const exists = await checkUrlExists(url);
            if (exists) {
              try {
                const response = await fetch(url, { mode: "cors" });
                const blob = await response.blob();

                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;

                const filename = evidence.url.split("/").pop();
                link.setAttribute("download", filename);

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
              } catch (err) {
                setSnackbarMessage("Error al descargar el archivo.");
                setSnackbarOpen(true);
              }
            } else {
              setSnackbarMessage("La evidencia no está disponible o no existe.");
              setSnackbarOpen(true);
            }
          }}>
          Descargar archivo
        </a>
      ),

      // Enlace de Google Drive
      actions: (
        <MDBox>
          {/*  <MDButton variant="text" color="info">
              <EditOutlinedIcon />
            </MDButton> */}
          {canDelete && (
            <MDButton variant="text" color="error">
              <a
                data-id={evidence.id}
                data-description={evidence.description}
                onClick={handleRoleDelete}
                style={{ marginLeft: "8px", cursor: "pointer" }}>
                <DeleteForeverOutlinedIcon
                  titleAccess="Eliminar"
                  style={{
                    fontSize: "200px",
                    width: "24px",
                    height: "24px",
                    color: darkMode ? white : iconColor,
                  }}
                />
              </a>
            </MDButton>
          )}
        </MDBox>
      ),
    }))
    : [];

  const navigate = useNavigate();
  const location = useLocation(); // Obtener el estado de la navegación
  const { id: activityId } = useParams(); // Obtener el ID de la actividad desde la URL

  const activityTitle = location.state?.activityTitle || ""; // Recuperar el título de la actividad desde location.state

  const handleNavigateToParticipants = () => {
    navigate(`/Actividades-P/${activityId}`, {
      state: { activityTitle }, // Pasar el título como parte del estado
    });
  };

  return (
    <>
      <Box sx={{
        mt: isMobile ? 2 : -6,
        mb: 3,
        px: { xs: 1, sm: 2, md: 3 },
        width: '100%'
      }}>
        <Grid container spacing={1} justifyContent={isMobile ? "center" : "flex-end"} alignItems="center" sx={{ gap: 0 }}>
          {canListParticipant && (
            <Grid item>
              <MDButton
                variant="outlined"
                color="info"
                onClick={handleNavigateToParticipants} // Llamar a la función para navegar
                startIcon={<VisibilityIcon />}
                endIcon={<ArrowForwardIcon />}>
                Ver participantes de la actividad
              </MDButton>
            </Grid>
          )}

          {canCreate && (
            <Grid item>
              <MDButton onClick={handleEvidenceCreate} variant="gradient" color="info">
                Crear
              </MDButton>
            </Grid>
          )}

        </Grid>
      </Box>
      {/* Mostrar la tabla con DataTable */}
      <DataTable
        table={{ columns, rows }}
        isSorted={true} // Habilitar ordenamiento
        entriesPerPage={false} // Mostrar control de entradas por página
        showTotalEntries={false} // Mostrar el total de entradas
        pagination={{ variant: "gradient", color: "info" }} // Estilo de paginación
      />
      <>
        {/* Mostrar la paginación */}
        <MDBox style={{ marginLeft: "20px" }} mb={{ xs: 3, sm: 0 }}>
          <MDTypography
            style={{ left: "20px" }}
            variant="button"
            fontWeight="regular">
            Mostrando {entriesStart} hasta {entriesEnd} de {totalData} resultados en total
          </MDTypography>
        </MDBox>
        <Stack style={{ marginLeft: "20px", marginBottom: "10px" }} spacing={2}>
          <DACIPagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
          />
        </Stack>{" "}
      </>
      {/* Snackbar para mostrar mensajes de alerta */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="error">
          <AlertTitle>Fallo al realizar la solicitud</AlertTitle>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
